import React from 'react';
import moment from 'moment';
import { debounce } from '../../helpers/util';
import { ChefAvailabilityContext } from './ChefAvailabilityContext.js';
import ChefAvailabilityCalendar from './ChefAvailabilityCalendar';
import ChefAvailabilityList from './ChefAvailabilityList';
import {
  validateDuration,
  validateEndDate,
  validateStartDate
} from '../../helpers/hourOptions';

class ChefAvailability extends React.Component {
  state = {
    activeAvailability: null,
    availabilities: [],
    rawAvailabilities: [],
    displayError: false
  };

  constructor(props) {
    super(props);
    this.state.deleteActive = this.deleteActive;
    this.state.clearActive = this.clearActive;
  }

  render() {
    const { isMobile = false } = this.props;
    return (
      <ChefAvailabilityContext.Provider value={this.state}>
        {isMobile ? this.renderList() : this.renderCalendar()}
        <p className="p small p--pad-top">*All times are EST</p>
        {this.state.displayError ? (
          <p className="p p--red">Must be at least 4 hours.</p>
        ) : (
          ''
        )}
      </ChefAvailabilityContext.Provider>
    );
  }

  renderCalendar() {
    const { isEdit = true, className } = this.props;
    const selectable = isEdit ? 'ignoreEvents' : false;
    const onSelectSlot = isEdit ? this.selectSlot : () => {};
    const onSelectEvent = isEdit ? this.selectEvent : () => {};

    return (
      <ChefAvailabilityCalendar
        selectable={selectable}
        onSelectSlot={onSelectSlot}
        onSelectEvent={onSelectEvent}
        className={className}
      />
    );
  }

  renderList() {
    const days = Array.from(Array(7).keys());
    const rawAvailabilities = this.state.rawAvailabilities || [];
    const list = days.map(day =>
      rawAvailabilities.filter(a => a.day_of_week === day)
    );

    return <ChefAvailabilityList list={list} showMobileWarning={true} />;
  }

  static jsonToActual(availability) {
    const start = moment()
      .day(availability.day_of_week)
      .hour(availability.start_time.split(':')[0] || 0)
      .minute(availability.start_time.split(':')[1] || 0)
      .toDate();
    const end = moment()
      .day(availability.day_of_week)
      .hour(availability.end_time.split(':')[0] || 0)
      .minute(availability.end_time.split(':')[1] || 0)
      .toDate();
    return { start, end, availability };
  }

  static actualToJson({ start, end }) {
    const day_of_week = moment(start).day();
    const start_time = moment(start).format('HH[:]mm');
    const end_time = moment(end).format('HH[:]mm');
    return { day_of_week, start_time, end_time };
  }

  selectEvent = (slot, e) => {
    if (e.target.className.startsWith('rbc-event'))
      this.setState({ activeAvailability: slot });
  };

  deleteActive = () => {
    this.deleteSlot(this.state.activeAvailability);
  };

  clearActive = () => {
    this.setState({ activeAvailability: null });
  };

  deleteSlot = slot => {
    const availabilities = this.state.availabilities.filter(s => s !== slot);
    const chef_availabilities = availabilities.map(a => a.availability);
    this.props.updateChef({ chef_availabilities });
  };

  selectSlot = ({ start, end }) => {
    // validate within hours of operation
    start = validateStartDate(start);
    end = validateEndDate(end);

    const availability = ChefAvailability.actualToJson({ start, end });
    const slot = { availability, start, end };

    const overlapping = this.state.availabilities.filter(existing =>
      this.findOverlappingSlots(slot, existing)
    );
    let chef_availabilities = [];
    if (overlapping.length > 0) {
      overlapping.push(slot);
      const newSlot = this.mergeOverlap(overlapping);
      // validate at least 4hr length
      if (!validateDuration(newSlot.start, newSlot.end)) {
        this.showError();
        return;
      }
      const chefAvailabilities = [...this.state.availabilities].filter(
        a => !overlapping.includes(a)
      );
      // add merged events
      chefAvailabilities.push(newSlot);
      chef_availabilities = chefAvailabilities.map(a => a.availability);
    } else {
      chef_availabilities = [
        ...this.state.rawAvailabilities,
        slot.availability
      ];
      // validate at least 4hr length
      if (!validateDuration(slot.start, slot.end)) {
        this.showError();
        return;
      }
    }

    // TODO: reorg so we only validate duration in one place

    this.props.updateChef({ chef_availabilities });
  };

  showError = () => {
    this.setState({ displayError: true }, this.debouncedHideError);
  };
  hideError = () => {
    this.setState({ displayError: false });
  };
  debouncedHideError = debounce(() => this.hideError(), 5000);

  mergeOverlap = overlapping => {
    const newStart = overlapping.sort(this.firstStart)[0];
    const newEnd = overlapping.sort(this.lastEnd)[0];

    return {
      availability: ChefAvailability.actualToJson({
        start: newStart.start,
        end: newEnd.end
      }),
      start: newStart.start,
      end: newEnd.end
    };
  };

  /**
   * Sorts earliest start date first
   */
  firstStart = (a, b) => {
    if (a.start > b.start) return 1;
    if (a.start < b.start) return -1;
    return 0;
  };

  /**
   * Sorts oldest end date first
   */
  lastEnd = (a, b) => {
    if (a.end > b.end) return -1;
    if (a.end < b.end) return 1;
    return 0;
  };

  findOverlappingSlots = (newSlot, existingSlot) => {
    newSlot.end.setSeconds(0, 0);
    newSlot.start.setSeconds(0, 0);
    existingSlot.end.setSeconds(0, 0);
    existingSlot.start.setSeconds(0, 0);

    // same day and start/end is within an existing slot
    return (
      newSlot.availability.day_of_week ===
        existingSlot.availability.day_of_week &&
      ((newSlot.start >= existingSlot.start &&
        newSlot.start <= existingSlot.end) || // starts within existing
      (newSlot.end >= existingSlot.start && newSlot.end <= existingSlot.end) || // ends within existing
        (newSlot.start <= existingSlot.start &&
          newSlot.end >= existingSlot.end)) // envelops existing
    );
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    let state = null;
    if (
      nextProps.availabilities &&
      nextProps.availabilities !== prevState.rawAvailabilities
    ) {
      const availabilities = nextProps.availabilities.map(availability =>
        ChefAvailability.jsonToActual(availability)
      );
      state = { availabilities, rawAvailabilities: nextProps.availabilities };
    }

    return state;
  }
}
export default ChefAvailability;



// WEBPACK FOOTER //
// ./src/components/chef/ChefAvailability.js